import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Button } from '@daedalus/atlas/Button';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Icon } from '@daedalus/atlas/Icon';
import { InputButton } from '@daedalus/atlas/InputButton';
import { Text } from '@daedalus/atlas/Text';
import { Tooltip, TooltipPlacement } from '@daedalus/atlas/Tooltip';
import { Action, Category, Entity, TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { dateStringToMiddayDate } from '@daedalus/core/src/utils/date';
import { datePickerMessages } from '../DatePicker/helpers/datePickerMessages';
import { guestPickerMessages } from '../GuestPicker/guestPickerMessages';
import { SearchFormDestinationInput } from './SearchFormDestinationInput';
import { IconWrapper } from './styles';
interface BaseProps {
  /** Check in date */
  checkIn: string;
  /** Check out date */
  checkOut: string;
  /** Rooms configuration */
  numberOfAdults: number;
  /** Rooms configuration */
  numberOfChildren: number;
  /** Rooms configuration */
  numberOfRooms: number;
  /** Callback for check-in date picker click */
  onCheckInDatePickerClick: () => void;
  /** Callback for check-out date picker click */
  onCheckOutDatePickerClick: () => void;
  /** Callback for Guest picker click */
  onGuestPickerClick: () => void;
  /** Callback for Submit click */
  onSubmit: () => void;
  /** Whether the check in picker popover is currently open */
  isCheckInPickerOpen: boolean;
  /** Whether the check out picker popover is currently open */
  isCheckOutPickerOpen: boolean;
  /** Whether the guest picker popover is currently open */
  isGuestPickerOpen: boolean;
  /** A ref to the guest picker container */
  guestPickerRef: React.RefObject<HTMLDivElement>;
  /** Whether the rooms count and label is shown */
  isRoomsLabelShown?: boolean;
  // Added as part of ab519a8e-date-confirmation-tooltip
  /** Wether user directly land on Search Page */
  isUserLandingDirectlyOnSRP?: boolean;
  /** Track event function. Temporary solution to track events without crashing sem-pages until they add redux analytics middleware.*/
  trackEvent: (payload: TrackEventPayload) => void;
}
interface PropsWithDestination extends BaseProps {
  /** Destination name displayed in the destination button */
  destinationButtonDisplayValue: string;
  /** Destination name displayed in the destination input */
  destinationInputDisplayValue: string;
  /** Callback for Destination picker click */
  onDestinationPickerClick: (event: React.SyntheticEvent<Element, Event>) => void;
  /** A ref to the destination input container */
  destinationInputRef: React.RefObject<HTMLDivElement>;
  /** Whether the destination picker popover is currently open */
  isDestinationPickerOpen: boolean;
  /** Callback for suggestions requested */
  onSuggestionsRequested: (value: string, shouldLoadUserSearchHistory: boolean) => void;
  /** A flag to see if the destination input has error */
  destinationHasError?: boolean;
}
interface PropsWithoutDestination extends BaseProps {
  destinationButtonDisplayValue?: never;
  destinationInputDisplayValue?: never;
  onDestinationPickerClick?: never;
  destinationInputRef?: never;
  isDestinationPickerOpen?: never;
  onSuggestionsRequested?: never;
  destinationHasError?: never;
}
type Props = PropsWithDestination | PropsWithoutDestination;
const FormContainer = /*#__PURE__*/styled('div')({
  name: "FormContainer",
  class: "f1ethwgy",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => Grid;
const SearchFormGrid = /*#__PURE__*/styled(_exp())({
  name: "SearchFormGrid",
  class: "s17qmoaa",
  propsAsIs: true
});
const _exp5 = /*#__PURE__*/() => Grid;
const DestinationWrapper = /*#__PURE__*/styled(_exp5())({
  name: "DestinationWrapper",
  class: "d1szhpff",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => Grid;
const DateWrapper = /*#__PURE__*/styled(_exp6())({
  name: "DateWrapper",
  class: "d12cti0w",
  propsAsIs: true
});
const ToolTipAnchor = /*#__PURE__*/styled('div')({
  name: "ToolTipAnchor",
  class: "t4nc7rl",
  propsAsIs: false
});
const ToolTipWrapper = /*#__PURE__*/styled('div')({
  name: "ToolTipWrapper",
  class: "t7lbo83",
  propsAsIs: false
});
const _exp15 = /*#__PURE__*/() => Grid;
const GuestWrapper = /*#__PURE__*/styled(_exp15())({
  name: "GuestWrapper",
  class: "g10e1chb",
  propsAsIs: true
});
const Divider = /*#__PURE__*/styled('div')({
  name: "Divider",
  class: "dlausq4",
  propsAsIs: false
});
const _exp19 = /*#__PURE__*/() => InputButton;
const FormButton = /*#__PURE__*/styled(_exp19())({
  name: "FormButton",
  class: "f1k0frrn",
  propsAsIs: true
});
const SearchButtonWrapper = /*#__PURE__*/styled('div')({
  name: "SearchButtonWrapper",
  class: "siqcgth",
  propsAsIs: false
});
const ButtonContent = /*#__PURE__*/styled('div')({
  name: "ButtonContent",
  class: "bjb7lft",
  propsAsIs: false
});
export const destinationPickerMessages = defineMessages({
  destinationPlaceholder: {
    id: 'whereAreYouGoing',
    defaultMessage: 'Where are you going?'
  }
});

// Added as part of ab519a8e-date-confirmation-tooltip
const SearchDateToolTipWrapper: React.FC<Pick<BaseProps, 'isUserLandingDirectlyOnSRP' | 'trackEvent'>> = ({
  children,
  isUserLandingDirectlyOnSRP = false,
  trackEvent
}) => {
  const [isInteracted, setIsInteracted] = useState(false);
  const anchorRef = useRef(null);
  const closeTooltip = () => setIsInteracted(true);
  useEffect(() => {
    if (!isInteracted) {
      document.addEventListener('pointerdown', closeTooltip);
      document.addEventListener('scroll', closeTooltip);
    }
    return () => {
      document.removeEventListener('pointerdown', closeTooltip);
      document.removeEventListener('scroll', closeTooltip);
    };
  }, [isInteracted]);
  useEffect(() => {
    if (isUserLandingDirectlyOnSRP) {
      trackEvent({
        category: Category.System,
        entity: Entity.SearchDateToolTip,
        action: Action.Displayed
      });
    }
  }, [isUserLandingDirectlyOnSRP, trackEvent]);
  if (!isUserLandingDirectlyOnSRP || isInteracted) {
    return <>{children}</>;
  }
  return <>
      <Tooltip isOpen={Boolean(!isInteracted && anchorRef.current)} anchor={anchorRef.current} placement={TooltipPlacement.BottomStart} onClose={closeTooltip} content={<Box width="271px">
            <Text variant="bodyS">
              <FormattedMessageWrapper id="confirmDates" defaultMessage="Remember to double check your dates" />
            </Text>
          </Box>}>
        {/* Empty element has to be there otherwise there gonna be some issues with the content that being rendered by Tooltip */}
        <div></div>
      </Tooltip>
      <ToolTipWrapper onMouseEnter={closeTooltip}>
        {children}
        <ToolTipAnchor ref={anchorRef} />
      </ToolTipWrapper>
    </>;
};
export const SearchFormHorizontal = React.memo(function SearchFormHorizontal({
  checkIn,
  checkOut,
  destinationButtonDisplayValue,
  destinationInputDisplayValue,
  numberOfAdults,
  numberOfChildren,
  numberOfRooms,
  onDestinationPickerClick,
  onCheckInDatePickerClick,
  onCheckOutDatePickerClick,
  onGuestPickerClick,
  onSubmit,
  isDestinationPickerOpen,
  isCheckInPickerOpen,
  isCheckOutPickerOpen,
  isGuestPickerOpen,
  destinationInputRef,
  guestPickerRef,
  onSuggestionsRequested,
  destinationHasError = false,
  isRoomsLabelShown = true,
  isUserLandingDirectlyOnSRP = false,
  trackEvent
}: Props) {
  const {
    formatDate,
    formatMessage
  } = useIntl();
  const showDestination = Boolean(onDestinationPickerClick);
  const numberOfGuests = numberOfAdults + numberOfChildren;
  const guestMessage = formatMessage(guestPickerMessages.guest, {
    count: numberOfGuests
  });
  const roomsMessage = formatMessage(guestPickerMessages.room, {
    count: numberOfRooms
  });
  const checkInDate = checkIn && formatDate(dateStringToMiddayDate(checkIn), {
    day: '2-digit',
    month: 'short'
  });
  const checkOutDate = checkOut && formatDate(dateStringToMiddayDate(checkOut), {
    day: '2-digit',
    month: 'short'
  });
  return <FormContainer className="search-form-horizontal-container">
      <SearchFormGrid container spacing="s000" wrap="nowrap" className="search-form-horizontal-grid">
        {showDestination && <>
            <DestinationWrapper>
              <FormButton size="md" id="DestinationButton" dataId="DestinationButton" onClick={onDestinationPickerClick} icon={<IconWrapper>
                    <Icon name="Pin" />
                  </IconWrapper>} isRounded placeholder={formatMessage(destinationPickerMessages.destinationPlaceholder)} className={cx(showDestination && 'isFirstInput', destinationHasError && 'hasError', isDestinationPickerOpen && 'isActive')}>
                {destinationButtonDisplayValue && <ButtonContent data-id="DestinationValue">
                    {destinationButtonDisplayValue}
                  </ButtonContent>}
              </FormButton>
              {isDestinationPickerOpen && <div ref={destinationInputRef}>
                  <SearchFormDestinationInput focusOnMount shouldLoadUserSearchHistory destination={destinationInputDisplayValue} onSuggestionsRequested={onSuggestionsRequested} />
                </div>}
            </DestinationWrapper>
            <Divider />
          </>}
        <SearchDateToolTipWrapper isUserLandingDirectlyOnSRP={isUserLandingDirectlyOnSRP} trackEvent={trackEvent}>
          <DateWrapper>
            <FormButton size="md" id="CheckInInput" dataId="CheckInInput" onClick={onCheckInDatePickerClick} icon={<IconWrapper>
                  <Icon name="CalendarStart" />
                </IconWrapper>} isRounded placeholder={formatMessage(datePickerMessages.checkInDate)} className={cx(!showDestination && 'isFirstInput', isCheckInPickerOpen && 'isActive', 'chromatic-ignore')}>
              {checkInDate && <ButtonContent>{checkInDate}</ButtonContent>}
            </FormButton>
          </DateWrapper>
          <Divider />
          <DateWrapper>
            <FormButton size="md" id="CheckOutInput" dataId="CheckOutInput" onClick={onCheckOutDatePickerClick} icon={<IconWrapper>
                  <Icon name="CalendarEnd" />
                </IconWrapper>} isRounded placeholder={formatMessage(datePickerMessages.checkOutDate)} className={cx(isCheckOutPickerOpen && 'isActive', 'chromatic-ignore')}>
              {checkOutDate && <ButtonContent>{checkOutDate}</ButtonContent>}
            </FormButton>
          </DateWrapper>
        </SearchDateToolTipWrapper>
        <Divider />
        <GuestWrapper>
          <div ref={guestPickerRef}>
            <FormButton size="md" id="GuestInput" dataId="GuestInput" onClick={onGuestPickerClick} icon={<IconWrapper>
                  <Icon name="People" />
                </IconWrapper>} isRounded className={cx(isGuestPickerOpen && 'isActive')}>
              <ButtonContent>
                {numberOfGuests} {guestMessage}
                {isRoomsLabelShown && <>
                    , {numberOfRooms} {roomsMessage}
                  </>}
              </ButtonContent>
            </FormButton>
          </div>
        </GuestWrapper>

        <Grid mobileSm="fit">
          <SearchButtonWrapper>
            <Button variant="primary" size="lg" dataId="SearchButton" onClick={onSubmit} iconStart={<Icon name="Search" />} isRounded>
              <FormattedMessageWrapper id="search" defaultMessage="Search" />
            </Button>
          </SearchButtonWrapper>
        </Grid>
      </SearchFormGrid>
    </FormContainer>;
});

require("./SearchFormHorizontal.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchFormHorizontal.tsx");